import React from 'react';
import { Box, Typography } from '@mui/material';

/**
 * Generic EmptyState Component
 * 
 * @param {Object} props
 * @param {React.Component} props.icon - MUI Icon component to display
 * @param {string} props.title - Title text for the empty state
 * @param {string} props.description - Description text for the empty state
 * @param {string} [props.iconClassName] - Optional custom class name for the icon container (default: 'empty-state-icon')
 * @param {string} [props.dotsClassName] - Optional custom class name for the animated dots (default: 'empty-state-dots')
 * @param {Object} [props.sx] - Additional sx props for the root container
 */
const EmptyState = ({
  icon: Icon,
  title,
  description,
  iconClassName = 'empty-state-icon',
  dotsClassName = 'empty-state-dots',
  sx = {},
}) => (
  <Box
    sx={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      textAlign: 'center',
      py: 8,
      ...sx,
    }}
  >
      {/* Interactive Icon Illustration */}
      <Box
        sx={{
          position: 'relative',
          mb: 4,
          [`&:hover .${iconClassName}`]: {
            transform: 'scale(1.1) rotate(-5deg)',
          },
          [`&:hover .${dotsClassName}`]: {
            opacity: 1,
            transform: 'translateY(-5px)',
          },
        }}
      >
        <Box
          className={iconClassName}
          sx={{
            width: { xs: 120, sm: 150, md: 180 },
            height: { xs: 120, sm: 150, md: 180 },
            borderRadius: '50%',
            backgroundColor: 'primary.main',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: (theme) => theme.customShadows?.primary || theme.customShadows?.card,
            transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
            position: 'relative',
            mb: 2,
          }}
        >
          {Icon && (
            <Icon
              sx={{
                fontSize: { xs: 60, sm: 75, md: 90 },
                color: 'primary.contrastText',
              }}
            />
          )}
        </Box>
      </Box>

      {/* Title */}
      <Typography
        variant="h4"
        sx={{
          fontWeight: 700,
          color: 'text.primary',
          mb: 2,
          fontSize: { xs: '1.5rem', sm: '2rem', md: '2.25rem' },
        }}
      >
        {title}
      </Typography>

      {/* Description */}
      <Typography
        variant="body1"
        sx={{
          color: 'text.secondary',
          mb: 4,
          maxWidth: '500px',
          lineHeight: 1.7,
          fontSize: { xs: '0.95rem', sm: '1rem' },
        }}
      >
        {description}
      </Typography>

      {/* Decorative elements */}
      <Box
        sx={{
          display: 'flex',
          gap: 2,
          mt: 2,
          opacity: 0.6,
        }}
      >
        {[...Array(3)].map((_, i) => (
          <Box
            key={i}
            sx={{
              width: 8,
              height: 8,
              borderRadius: '50%',
              backgroundColor: 'primary.main',
              animation: `bounce 1.5s ease-in-out ${i * 0.3}s infinite`,
              '@keyframes bounce': {
                '0%, 100%': {
                  transform: 'translateY(0)',
                },
                '50%': {
                  transform: 'translateY(-10px)',
                },
              },
            }}
          />
        ))}
      </Box>
    </Box>
);

export default EmptyState;

